import { type AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';

import type ParcelTemplate from '~/types/ParcelTemplate';

import { ActionKeys } from '~/actions/constants';
import * as ratesAtCheckoutActions from '~/actions/rates-at-checkout.actions';
import * as ratesAtCheckoutService from '~/services/rates-at-checkout.service';
import { type SavedAPIAddress } from '~/types/APIAddress';
import * as ratesAtCheckout from '~/types/RatesAtCheckout';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorDetail = (err: any) =>
  err?.response?.data?.detail ?? 'An error occurred. Please try again later.';

function* fetchShippingOptions() {
  try {
    const response: { data: ratesAtCheckout.ShippingOptionPayload[] } = yield call(
      ratesAtCheckoutService.fetchShippingOptions,
    );
    yield put(ratesAtCheckoutActions.fetchShippingOptionsSuccess(response));
  } catch (err) {
    yield put(ratesAtCheckoutActions.fetchShippingOptionsError(err.message));
  }
}

function* createShippingOption(
  action: ReturnType<typeof ratesAtCheckoutActions.createShippingOption>,
) {
  try {
    const response: { data: ratesAtCheckout.ShippingOptionPayload } = yield call(
      ratesAtCheckoutService.createShippingOption,
      action.payload,
    );
    yield put(ratesAtCheckoutActions.createShippingOptionSuccess(response));
    const updatedResp: { data: ratesAtCheckout.ShippingOptionPayload[] } = yield call(
      ratesAtCheckoutService.fetchShippingOptions,
    );
    yield put(ratesAtCheckoutActions.fetchShippingOptionsSuccess(updatedResp));
    yield put(ratesAtCheckoutActions.closeAddShippingOptionModal());
  } catch (err) {
    const errorArray = ratesAtCheckout.parseErrorObject(err.response.data);
    yield put(ratesAtCheckoutActions.createShippingOptionError(errorArray));
  }
}

function* updateShippingOption(
  action: ReturnType<typeof ratesAtCheckoutActions.updateShippingOption>,
) {
  try {
    const response: { data: ratesAtCheckout.ShippingOptionPayload } = yield call(
      ratesAtCheckoutService.updateShippingOption,
      action.payload,
    );
    yield put(ratesAtCheckoutActions.updateShippingOptionSuccess(response));
    const updatedResp: { data: ratesAtCheckout.ShippingOptionPayload[] } = yield call(
      ratesAtCheckoutService.fetchShippingOptions,
    );
    yield put(ratesAtCheckoutActions.fetchShippingOptionsSuccess(updatedResp));
    yield put(ratesAtCheckoutActions.closeAddShippingOptionModal());
  } catch (err) {
    const errorArray = ratesAtCheckout.parseErrorObject(err.response.data);
    yield put(ratesAtCheckoutActions.updateShippingOptionError(errorArray));
  }
}

function* deleteShippingOption(
  action: ReturnType<typeof ratesAtCheckoutActions.deleteShippingOption>,
) {
  try {
    const response: { data: string } = yield call(
      ratesAtCheckoutService.deleteShippingOption,
      action.payload,
    );
    yield put(ratesAtCheckoutActions.deleteShippingOptionSuccess(response));
    const updatedResp: { data: ratesAtCheckout.ShippingOptionPayload[] } = yield call(
      ratesAtCheckoutService.fetchShippingOptions,
    );
    yield put(ratesAtCheckoutActions.fetchShippingOptionsSuccess(updatedResp));
    yield put(ratesAtCheckoutActions.closeAddShippingOptionModal());
  } catch (err) {
    yield put(ratesAtCheckoutActions.deleteShippingOptionError(errorDetail(err)));
  }
}

function* fetchLiveRatePackage() {
  try {
    const response: AxiosResponse<{ result: ratesAtCheckout.BaseJSONParcelTemplate }> = yield call(
      ratesAtCheckoutService.getliveRatePackage,
    );

    let formattedParcelTemplates: AxiosResponse = response;
    if (response.data.result) {
      formattedParcelTemplates = {
        ...response,
        data: {
          result: ratesAtCheckout.fromJSON(response.data.result),
        },
      };
    }

    yield put(ratesAtCheckoutActions.fetchLiveRatePackageSuccess(formattedParcelTemplates));
  } catch (err) {
    yield put(ratesAtCheckoutActions.fetchLiveRatePackageError(errorDetail(err)));
  }
}

function* updateLiveRatePackage(
  action: ReturnType<typeof ratesAtCheckoutActions.updateLiveRatePackage>,
) {
  try {
    const updateResp: { data: ParcelTemplate } = yield call(
      ratesAtCheckoutService.updateLiveRatePackage,
      {
        object_id: action.payload,
      },
    );
    const response: AxiosResponse<{ result: ratesAtCheckout.BaseJSONParcelTemplate }> = yield call(
      ratesAtCheckoutService.getliveRatePackage,
    );
    let formattedParcelTemplates: AxiosResponse = response;
    if (response.data.result) {
      formattedParcelTemplates = {
        ...response,
        data: {
          result: ratesAtCheckout.fromJSON(response.data.result),
        },
      };
    }
    yield put(ratesAtCheckoutActions.fetchLiveRatePackageSuccess(formattedParcelTemplates));
    yield put(ratesAtCheckoutActions.updateLiveRatePackageSuccess(updateResp));
  } catch (err) {
    yield put(ratesAtCheckoutActions.updateLiveRatePackageError(errorDetail(err)));
  }
}

function* fetchLiveRateSenderAddress() {
  try {
    const response: AxiosResponse<Parameters<typeof ratesAtCheckout.fromJSONsenderAddress>[0]> =
      yield call(ratesAtCheckoutService.fetchLiveRateSenderAddress);
    const parsedResponseData = ratesAtCheckout.fromJSONsenderAddress(response?.data);
    yield put(ratesAtCheckoutActions.fetchLiveRateSenderAddressSuccess(parsedResponseData));
  } catch (err) {
    yield put(ratesAtCheckoutActions.fetchLiveRateSenderAddressError(errorDetail(err)));
  }
}

function* updateLiveRateSenderAddress(
  action: ReturnType<typeof ratesAtCheckoutActions.updateLiveRateSenderAddress>,
) {
  try {
    // Does this return camelCased? Most API responses return snake_cased. Please confirm!
    const response: AxiosResponse<SavedAPIAddress> = yield call(
      ratesAtCheckoutService.updateLiveRateSenderAddress,
      action.payload.liveRatesAddressId,
    );
    yield put(ratesAtCheckoutActions.updateLiveRateSenderAddressSuccess(response.data));
  } catch (err) {
    yield put(ratesAtCheckoutActions.updateLiveRateSenderAddressError(errorDetail(err)));
  }
}

export default [
  takeLatest(ActionKeys.RATES_AT_CHECKOUT_FETCH_SHIPPING_OPTIONS, fetchShippingOptions),
  takeLatest(ActionKeys.RATES_AT_CHECKOUT_CREATE_SHIPPING_OPTION, createShippingOption),
  takeLatest(ActionKeys.RATES_AT_CHECKOUT_UPDATE_SHIPPING_OPTION, updateShippingOption),
  takeLatest(ActionKeys.RATES_AT_CHECKOUT_DELETE_SHIPPING_OPTION, deleteShippingOption),
  takeLatest(ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_PACKAGE, fetchLiveRatePackage),
  takeLatest(ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_PACKAGE, updateLiveRatePackage),
  takeLatest(
    ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_SENDER_ADDRESS,
    fetchLiveRateSenderAddress,
  ),
  takeLatest(
    ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_SENDER_ADDRESS,
    updateLiveRateSenderAddress,
  ),
];
